
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useUserRole } from './useUserRole';

export interface ActivityItem {
  id: string;
  type: 'candidate' | 'job' | 'interview' | 'placement';
  title: string;
  description: string;
  timestamp: string;
  icon?: string;
}

export const useActivityFeed = () => {
  const { user } = useAuth();
  const { data: userRole } = useUserRole();

  return useQuery({
    queryKey: ['activity-feed', user?.id, userRole],
    queryFn: async (): Promise<ActivityItem[]> => {
      if (!user?.id) return [];

      console.log('Fetching activity feed for user:', user.id, 'with role:', userRole);

      const activities: ActivityItem[] = [];

      // With clean RLS policies, we can query directly
      // The organization-based policies will automatically filter the results

      // Get recent candidates
      const { data: candidates } = await supabase
        .from('candidates')
        .select('id, first_name, last_name, created_at, status')
        .order('created_at', { ascending: false })
        .limit(5);

      candidates?.forEach(candidate => {
        activities.push({
          id: `candidate-${candidate.id}`,
          type: 'candidate',
          title: 'New candidate added',
          description: `${candidate.first_name} ${candidate.last_name} - Status: ${candidate.status}`,
          timestamp: candidate.created_at,
        });
      });

      // Get recent jobs
      const { data: jobs } = await supabase
        .from('jobs')
        .select('id, title, company, created_at, status')
        .order('created_at', { ascending: false })
        .limit(5);

      jobs?.forEach(job => {
        activities.push({
          id: `job-${job.id}`,
          type: 'job',
          title: 'New job posted',
          description: `${job.title} at ${job.company} - Status: ${job.status}`,
          timestamp: job.created_at,
        });
      });

      // Get recent calendar events
      const { data: events } = await supabase
        .from('calendar_events')
        .select('id, title, start_time, created_at')
        .order('created_at', { ascending: false })
        .limit(5);

      events?.forEach(event => {
        activities.push({
          id: `interview-${event.id}`,
          type: 'interview',
          title: 'Interview scheduled',
          description: event.title,
          timestamp: event.created_at,
        });
      });

      // Sort all activities by timestamp
      const sortedActivities = activities
        .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
        .slice(0, 10); // Keep only the 10 most recent

      console.log('Activity feed fetched:', sortedActivities.length, 'items for role:', userRole);
      return sortedActivities;
    },
    enabled: !!user?.id && userRole !== undefined,
    staleTime: 30 * 1000, // 30 seconds
  });
};
